import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";
import {
  DealItemAllowance,
  DealItemMargin,
  DealItemPromoPrice,
  DealItemScan,
} from "src/dm/interfaces/DealItemSub";

interface Envelope<T> {
  items: T[];
  total: number;
}

const itemUrl = (dealId: number, dealItemId: number, suffix: string) =>
  `${APIS.Deals}/${dealId}/items/${dealItemId}/${suffix}`;

const makeHook = <T>(suffix: string, prefix: string) =>
  (dealId: number | undefined, dealItemId: number | undefined) =>
    useQuery({
      queryKey: [prefix, dealId, dealItemId],
      enabled:
        dealId !== undefined && dealId > 0 &&
        dealItemId !== undefined && dealItemId > 0,
      queryFn: async (): Promise<T[]> => {
        const response = await apiClient.get<Envelope<T>>(
          itemUrl(dealId as number, dealItemId as number, suffix)
        );
        return response.data.items;
      },
    });

export const useItemAllowances = makeHook<DealItemAllowance>("allowances", "item-allowances");
export const useItemScans = makeHook<DealItemScan>("scans", "item-scans");
export const useItemPromoPrices = makeHook<DealItemPromoPrice>("promo-prices", "item-promo-prices");
export const useItemMargins = makeHook<DealItemMargin>("margins", "item-margins");

// ---------------------------------------------------------------------------
// Promo-price write hooks
// ---------------------------------------------------------------------------
// The legacy ViewItem.aspx wired Amount Off / Price / % Off edits into a
// single PromoPricesAddOrUpdate code path. The new SPA splits the writes
// across REST verbs so the React Query cache stays sound; the read hook
// above is invalidated on every successful mutation.

/**
 * Write shape for POST/PUT promo-prices. Mirrors `DealItemPromoPriceWriteRequest`
 * on the API side — UseCurrentPrice is a bool here even though the column
 * stores Y/N. Note: AmountOff IS part of the payload (audit gap #3).
 */
export interface PromoPriceWriteRequest {
  zoneNumber: number;
  overrideNumber: number | null;
  storeNumber: number | null;
  multiUnitCount: number | null;
  price: number | null;
  amountOff: number | null;
  percentOff: number | null;
  specialPricing: string | null;
  promoGroupId: number | null;
  useCurrentPrice: boolean;
  regularRetailMarginBeforeScan: number | null;
  promoRetailMarginBeforeScan: number | null;
  promoRetailMarginAfterScan: number | null;
}

export const useUpdateItemPromoPrice = (
  dealId: number | undefined,
  dealItemId: number | undefined
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { promoPriceId: number; body: PromoPriceWriteRequest }) => {
      if (!dealId || !dealItemId) throw new Error("dealId / dealItemId required");
      const url = `${itemUrl(dealId, dealItemId, "promo-prices")}/${vars.promoPriceId}`;
      const response = await apiClient.put<DealItemPromoPrice>(url, vars.body);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["item-promo-prices", dealId, dealItemId] });
      // Margin numbers refresh too because the server runs MarginCalc.
      qc.invalidateQueries({ queryKey: ["item-margins", dealId, dealItemId] });
    },
  });
};

export const useCreateItemPromoPrice = (
  dealId: number | undefined,
  dealItemId: number | undefined
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PromoPriceWriteRequest) => {
      if (!dealId || !dealItemId) throw new Error("dealId / dealItemId required");
      const url = itemUrl(dealId, dealItemId, "promo-prices");
      const response = await apiClient.post<DealItemPromoPrice>(url, body);
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["item-promo-prices", dealId, dealItemId] });
      qc.invalidateQueries({ queryKey: ["item-margins", dealId, dealItemId] });
    },
  });
};

export const useDeleteItemPromoPrice = (
  dealId: number | undefined,
  dealItemId: number | undefined
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (promoPriceId: number) => {
      if (!dealId || !dealItemId) throw new Error("dealId / dealItemId required");
      const url = `${itemUrl(dealId, dealItemId, "promo-prices")}/${promoPriceId}`;
      await apiClient.delete(url);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["item-promo-prices", dealId, dealItemId] });
      qc.invalidateQueries({ queryKey: ["item-margins", dealId, dealItemId] });
    },
  });
};
